/**
 * Safe async data loading hook.
 * Ensures loading always resolves to success, empty, or error — never stuck.
 */

import { useState, useCallback, useEffect, useRef } from "react";

export type AsyncState<T> =
  | { status: "loading" }
  | { status: "success"; data: T }
  | { status: "empty"; data: T }
  | { status: "error"; error: unknown; data?: T };

export interface UseSafeAsyncDataOptions<T> {
  /** Fetch function — must not throw; return null/empty on failure */
  fetch: () => Promise<T>;
  /** Dependencies — refetch when these change */
  deps?: unknown[];
  /** Initial data while loading (optional) */
  initialData?: T;
  /** Treat empty result (e.g. null, []) as success, not error */
  emptyIsSuccess?: boolean;
  /** Called when fetch throws */
  onError?: (err: unknown) => void;
}

/**
 * Load data with guaranteed finalization: loading -> success | empty | error.
 * Never leaves loading=true indefinitely.
 */
export function useSafeAsyncData<T>(options: UseSafeAsyncDataOptions<T>): {
  state: AsyncState<T>;
  refetch: () => Promise<void>;
  isLoading: boolean;
  hasError: boolean;
} {
  const { fetch: fetchFn, deps = [], initialData, emptyIsSuccess = true, onError } = options;
  const [state, setState] = useState<AsyncState<T>>(() =>
    initialData != null ? { status: "success", data: initialData } : { status: "loading" }
  );
  const mountedRef = useRef(true);
  const abortRef = useRef(false);

  const doFetch = useCallback(async () => {
    abortRef.current = false;
    setState({ status: "loading" });

    try {
      const data = await fetchFn();

      if (abortRef.current) return;

      const isEmpty = data == null || (Array.isArray(data) && data.length === 0);
      if (isEmpty && !emptyIsSuccess) {
        setState({ status: "empty", data: data ?? ([] as T) });
      } else {
        setState({ status: "success", data });
      }
    } catch (err) {
      if (abortRef.current) return;
      onError?.(err);
      setState({ status: "error", error: err });
    }
  }, [fetchFn, emptyIsSuccess, onError]);

  useEffect(() => {
    mountedRef.current = true;
    void doFetch();
    return () => {
      mountedRef.current = false;
      abortRef.current = true;
    };
  }, deps);

  return {
    state,
    refetch: doFetch,
    isLoading: state.status === "loading",
    hasError: state.status === "error",
  };
}
